import React, { useState, useEffect } from 'react';
import { useAuth } from '../authentication/AuthContext';
import {  useLocation, useNavigate } from 'react-router-dom';
import { CreditsDisplay } from '../components/CreditsDisplay';
import { paymentApi } from '../services/paymentApi';
import { StripeWrapper } from '../components/StripeWrapper';
import { PaymentForm } from '../payment/PaymentForm';
import { SUBSCRIPTION_LIMITS } from '../payment/PricingUtils';
import '../index.css';

function MyAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    firstName, 
    email, 
    changePassword, 
    lastFreeResearch,
    hasActiveSubscription,
    subscriptionUntil,
    subscriptionCanceledDate,
    credits,
    updateUserAttributes,
    deleteAccount
  } = useAuth();

  // const { user, isAdmin } = useAuth(); // TODO: may use for admin-only features
  
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSubscribeForm, setShowSubscribeForm] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionCanceledDateState, setSubscriptionCanceledDate] = useState(subscriptionCanceledDate);
  const [creditsState, setCredits] = useState(credits);
  const [subscriptionUntilState, setSubscriptionUntil] = useState(subscriptionUntil);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  // Check for message in location state when component mounts
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
    
    // Show subscription form if coming from pricing page with subscribe param
    if (location.state?.subscribe) {
      setShowSubscribeForm(true);
    }
  }, [location]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    try {
      await changePassword(oldPassword, newPassword);
      setMessage('Password changed successfully!');
      setShowChangePassword(false);
      setOldPassword('');
      setNewPassword('');
    } catch (err) {
      setError(err.message || 'Failed to change password');
    }
  };

  const getNextFreeResearchDate = () => {
    if (!lastFreeResearch) return 'Available now';
    const lastDate = new Date(lastFreeResearch);
    const nextDate = new Date(lastDate.getTime() + (7 * 24 * 60 * 60 * 1000));
    return nextDate > new Date() ? 
      `Available on ${nextDate.toLocaleDateString()}` : 
      'Available now';
  };

  const handleDeleteAccount = async () => {
    setDeleteInProgress(true);
    setDeleteError('');
    
    try {
      await deleteAccount();
      // Redirect to home page after successful deletion
      navigate('/');
    } catch (error) {
      setDeleteError('Failed to delete account. Please try again.');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will still have access until your current billing period ends.')) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      await paymentApi.cancelSubscription();
      setMessage('Your subscription has been canceled. You will still have access until the end of your current billing period.');
      
      // Refresh user attributes to update the UI
      const updatedUserData = await updateUserAttributes();
      
      // Force a re-render by explicitly updating state with the fresh data
      if (updatedUserData) {
        setSubscriptionCanceledDate(updatedUserData.subscriptionCanceledDate);
        setCredits(updatedUserData.credits);
        setSubscriptionUntil(updatedUserData.subscriptionUntil);
      }
    } catch (err) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!window.confirm('Would you like to reactivate your subscription? You will continue to be billed after your current period ends.')) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await paymentApi.reactivateSubscription();
      setMessage(result.message || 'Your subscription has been reactivated and will automatically renew.');
      
      // Refresh user attributes to update the UI
      const updatedUserData = await updateUserAttributes();
      
      // Force a re-render by explicitly updating state with the fresh data
      if (updatedUserData) {
        setSubscriptionCanceledDate(updatedUserData.subscriptionCanceledDate);
        setCredits(updatedUserData.credits);
        setSubscriptionUntil(updatedUserData.subscriptionUntil);
      }
    } catch (err) {
      console.error('Reactivation error:', err);
      
      // If the error indicates the subscription is already active
      if (err.message?.includes('not scheduled to be canceled') || err.alreadyActive) {
        setMessage('Your subscription is already active. The display will be updated.');
        
        // Force update the local state since server says it's already active
        const updatedUserData = await updateUserAttributes();
        if (updatedUserData) {
          setSubscriptionCanceledDate(null);
          setCredits(updatedUserData.credits);
          setSubscriptionUntil(updatedUserData.subscriptionUntil);
        }
      } else {
        setError(err.message || 'Failed to reactivate subscription');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshSubscriptionStatus = async () => {
    setIsLoading(true);
    try {
      await updateUserAttributes();
      setMessage("Account information refreshed successfully.");
    } catch (error) {
      setError("Failed to refresh subscription status.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscriptionCheckout = async () => {
    setError('');
    setIsLoading(true);
    try {
      const sessionId = await paymentApi.createSubscription();
      setIsLoading(false);
      return sessionId;
    } catch (err) {
      setError(err.message || 'Failed to create subscription');
      setIsLoading(false);
      throw err;
    }
  };

  const handlePaymentError = (error) => {
    setError(error.message || 'Payment processing failed');
    setIsLoading(false);
  };

  const renderChangePassword = () => (
    <div className="container">
      <h2>Change Password</h2>
      <form onSubmit={handleChangePassword}>
        <div className="form-group">
          <label htmlFor="oldPassword">Current Password:</label>
          <input 
            type="password" 
            id="oldPassword" 
            value={oldPassword} 
            onChange={(e) => setOldPassword(e.target.value)} 
            required 
          />
          <label htmlFor="newPassword">New Password:</label>
          <input 
            type="password" 
            id="newPassword" 
            value={newPassword} 
            onChange={(e) => setNewPassword(e.target.value)} 
            required 
          />
        </div>
        <button type="submit" className="button standout-button">
          Update Password
        </button>
        <button 
          type="button" 
          onClick={() => setShowChangePassword(false)} 
          className="button secondary-button"
        >
          Cancel
        </button>
      </form>
    </div>
  );

  return (
    <div className="login-page">
      <div className="hero hero-1 top-hero">
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}
        
        <div className="container">
          <h2>Account Details</h2>
          <p><strong>Name:</strong> {firstName || 'Not set'}</p>
          <p><strong>Email:</strong> {email || 'Not set'}</p>
          <button 
            className="button"
            onClick={() => setShowChangePassword(!showChangePassword)}
          >
            Change Password
          </button>

          <button 
            className="button secondary-button"
            onClick={handleRefreshSubscriptionStatus}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh Status'}
          </button>
        </div>

        {showChangePassword && renderChangePassword()}

        {/* Research Account Section */}
        <div className="container">
          <h2>Research Account</h2>
          <CreditsDisplay />
          
          {/* Subscription information */}
          {hasActiveSubscription ? (
            <div className="subscription-info">
              <h3>Premium Subscription</h3>
              {subscriptionCanceledDateState ? (
                <>
                  <p>
                    <strong>Status:</strong> 
                    <span className="canceled-badge">Canceled - Access until {new Date(subscriptionUntilState).toLocaleDateString()}</span>
                  </p>
                  <p className="subscription-note">
                    <strong>Note:</strong> Your subscription has been canceled but remains active until {new Date(subscriptionUntilState).toLocaleDateString()}. 
                    You will continue to receive weekly credit refreshes until this date.
                  </p>
                </>
              ) : (
                <>
                  <p><strong>Status:</strong> <span className="active-badge">Active</span></p>
                  <p><strong>Renews:</strong> {new Date(subscriptionUntilState).toISOString().split('T')[0]}</p>
                </>
              )}
              
              <p><strong>Weekly Researches:</strong> {creditsState} of {SUBSCRIPTION_LIMITS.WEEKLY_RESEARCHES} remaining</p>
              <p><strong>Research Size:</strong> Up to {SUBSCRIPTION_LIMITS.MAX_QUESTIONS} questions and {SUBSCRIPTION_LIMITS.MAX_PERSONAS} personas</p>
              
              <div className="button-group">
                {!subscriptionCanceledDateState && (
                  <button 
                    className="button secondary-button"
                    onClick={handleCancelSubscription}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Cancel Subscription'}
                  </button>
                )}
                {subscriptionCanceledDateState && (
                  <button 
                    className="button standout-button"
                    onClick={handleReactivateSubscription}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Reactivate Subscription'}
                  </button>
                )}
              </div>
              <p className="subscription-note">
                {subscriptionCanceledDateState ? 
                  'You can resubscribe anytime to prevent losing access.' : 
                  'Your subscription will automatically renew. You can cancel anytime and will maintain access until the end of the current billing period.'}
              </p>
            </div>
          ) : (
            <>
              <h3>Free Research Plan</h3>
              <p><strong>Credits:</strong> {creditsState} of 1 per week</p>
              <p><strong>Next Free Research:</strong> <br/> {getNextFreeResearchDate()}</p>
             
              {showSubscribeForm ? (
                <StripeWrapper>
                    <h3>Subscribe:</h3>
                    <p>€20/month</p>
                    <PaymentForm 
                      amount={20}
                      onSuccess={handleSubscriptionCheckout}
                      onError={handlePaymentError}
                      selectedPlan="SUBSCRIPTION"
                    />
                    <button 
                      className="button secondary-button"
                      onClick={() => setShowSubscribeForm(false)}
                    >
                      Cancel
                    </button>
                </StripeWrapper>

              ) : (
                <div className="subscription-actions">
                  <button 
                    className="button standout-button"
                    onClick={() => setShowSubscribeForm(true)}
                  >
                    Subscribe Now
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="account-section danger-zone">
          <h3>Delete Account</h3>
          <p>
            Warning: This action cannot be undone. All your data will be permanently deleted.
            {hasActiveSubscription && " Your subscription will be cancelled immediately."}
          </p>
          
          {!showDeleteConfirm ? (
            <button 
              className="button standout-button"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Account
            </button>
          ) : (
            <div className="delete-confirmation">
              <p>Are you sure you want to delete your account?</p>
              <div className="button-group">
                <button 
                  className="button standout-button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteInProgress}
                >
                  Cancel
                </button>
                <button 
                  className="button standout-button"
                  onClick={handleDeleteAccount}
                  disabled={deleteInProgress}
                >
                  {deleteInProgress ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
              {deleteError && <p className="error-message">{deleteError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyAccountPage;